import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Album, AlbumMemoryVideo, AlbumMemoryVideoTemplate } from "../types/album";
import { useUser, useMedia, useAlbumUploaders, useAlbumLikes, useAlbumLinks, useAlbumPermissions, usePickMediaForUpload } from "../hooks";
import MediaGallery from "./MediaGallery";
import UploadPrepareOverlay from "./UploadPrepareOverlay";
import { ErrorBoundary, CommentsBottomSheet, AlbumLikesModal, OverlayRoot, ShareLinksModal } from "./shared";
import { logError } from "../utils/errorHandler";
import { albumService } from "../services/albumService";
import { fetchAuthenticatedRaw } from "../services/tokenHelper";
import { useUploadQueue } from "../contexts/UploadQueueContext";
import DeleteMediaSelectionModal from "./DeleteMediaSelectionModal";
import { DEFAULT_MEDIA_SORT, MediaSortBy, MediaSortOptions, SortOrder } from "../types/mediaSort";
import { albumMediaUploaderFilterLabel, albumMediaUploaderLabel, isAlbumUploaderId, toggleAlbumUploaderFilter } from "../types/media";
import { albumKeys } from "../hooks/useAlbumsQuery";
import { albumDebug } from "../utils/albumDebug";

interface AlbumDetailProps {
  album: Album;
  onBack?: () => void;
}

const AlbumDetail: React.FC<AlbumDetailProps> = ({
  album,
  onBack: onBackProp,
}) => {
  const isMemoriesVideoEnabled =
    import.meta.env.VITE_ENABLE_MEMORIES_VIDEO === "true";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showComments, setShowComments] = useState(false);
  const [showLikes, setShowLikes] = useState(false);
  const [showShareLinks, setShowShareLinks] = useState(false);
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedMediaIds, setSelectedMediaIds] = useState<Set<string>>(new Set());
  const [isDownloadingSelection, setIsDownloadingSelection] = useState(false);
  const [downloadSelectionError, setDownloadSelectionError] = useState<string | null>(null);
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const actionsMenuRef = useRef<HTMLDivElement | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const sortMenuRef = useRef<HTMLDivElement | null>(null);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const filterMenuRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [isDeleteSelectionModalOpen, setIsDeleteSelectionModalOpen] = useState(false);
  const [isDeletingSelection, setIsDeletingSelection] = useState(false);
  const [deleteSelectionError, setDeleteSelectionError] = useState<string | null>(null);
  const [isScrollCompactHeader, setIsScrollCompactHeader] = useState(false);
  const lastSelectedMediaIdRef = useRef<string | null>(null);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [renameValue, setRenameValue] = useState(album.name);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [isRenamingAlbum, setIsRenamingAlbum] = useState(false);
  const [memoryVideoJob, setMemoryVideoJob] = useState<AlbumMemoryVideo | null>(null);
  const [isMemoryVideoLoading, setIsMemoryVideoLoading] = useState(false);
  const [isCreatingMemoryVideo, setIsCreatingMemoryVideo] = useState(false);
  const [memoryVideoError, setMemoryVideoError] = useState<string | null>(null);
  const [isMemoryVideoModalOpen, setIsMemoryVideoModalOpen] = useState(false);
  const [selectedMemoryVideoTemplate, setSelectedMemoryVideoTemplate] =
    useState<AlbumMemoryVideoTemplate>("calm");

  const { user } = useUser();
  const { enqueue } = useUploadQueue();
  const handleFilesReadyForUpload = useCallback((files: File[]) => {
    if (files.length === 0) return;
    enqueue({ ownerId: album.ownerId, albumId: album.id, files });
  }, [enqueue, album.ownerId, album.id]);
  const {
    status: uploadPrepareStatus,
    isPreparing: isPreparingUpload,
    showPrepareUi,
    openPicker,
    cancel: cancelPicker,
    fileInputRef,
    acceptedTypes: uploadAcceptedTypes,
    handleFileInputChange,
  } = usePickMediaForUpload({
    onFilesReady: handleFilesReadyForUpload,
  });
  const canRenameAlbum = user?.id === album.ownerId;
  const memoryVideoTemplateLabel =
    memoryVideoJob?.template === "energetic" ? "Energetic" : "Calm";
  const memoryVideoStatus = memoryVideoJob?.status ?? null;
  const isMemoryVideoProcessing =
    memoryVideoStatus === "queued" || memoryVideoStatus === "processing";
  const memoryVideoButtonLabel =
    isMemoryVideoLoading && !memoryVideoJob
      ? "Checking Video..."
      : memoryVideoJob?.status === "done" && memoryVideoJob.url
        ? "View Memories Video"
        : memoryVideoJob?.status === "failed"
          ? "Retry Memories Video"
          : isCreatingMemoryVideo || isMemoryVideoProcessing
            ? "Creating Video..."
            : "Create Memories Video";
  const compactMemoryVideoLabel =
    memoryVideoJob?.status === "done" && memoryVideoJob.url ? "View Video" : "Memories";
  const memoryVideoStatusMessage = (() => {
    if (memoryVideoError) return memoryVideoError;
    if (!memoryVideoJob) return null;
    if (memoryVideoJob.status === "queued") {
      return `${memoryVideoTemplateLabel} memories video queued. This page will update automatically.`;
    }
    if (memoryVideoJob.status === "processing") {
      return `Creating your ${memoryVideoTemplateLabel.toLowerCase()} memories video now.`;
    }
    if (memoryVideoJob.status === "done" && memoryVideoJob.url) {
      return memoryVideoJob.soundtrack
        ? `${memoryVideoTemplateLabel} memories video ready with soundtrack ${memoryVideoJob.soundtrack}.`
        : `${memoryVideoTemplateLabel} memories video is ready to view.`;
    }
    if (memoryVideoJob.status === "failed") {
      return memoryVideoJob.error || "Memories video generation failed.";
    }
    return null;
  })();
  const memoryVideoStatusClassName =
    memoryVideoJob?.status === "failed" || memoryVideoError
      ? "text-red-300"
      : memoryVideoJob?.status === "done"
        ? "text-green-300"
        : "text-gray-300";
  const MAX_NATIVE_SAVE_FILES = 10;
  const canNativeSaveSelection =
    typeof navigator !== "undefined" &&
    /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) &&
    typeof navigator.share === "function" &&
    typeof navigator.canShare === "function" &&
    typeof File === "function";

  // Check permissions for this album
  const { canUpload, canDelete, canDeleteMedia, canComment, canLike } = useAlbumPermissions(
    album.ownerId,
    album.id,
  );

  const sort: MediaSortOptions = (() => {
    const rawSortBy = searchParams.get("sortBy") as MediaSortBy | null;
    const rawSortOrder = searchParams.get("sortOrder") as SortOrder | null;

    const sortBy: MediaSortBy =
      rawSortBy === "uploadedAt" || rawSortBy === "takenAt" || rawSortBy === "name"
        ? rawSortBy
        : DEFAULT_MEDIA_SORT.sortBy;

    const sortOrder: SortOrder = rawSortOrder === "asc" || rawSortOrder === "desc" ? rawSortOrder : DEFAULT_MEDIA_SORT.sortOrder;

    return { sortBy, sortOrder };
  })();

  const setSort = useCallback(
    (next: MediaSortOptions) => {
      const params = new URLSearchParams(searchParams);
      params.set("sortBy", next.sortBy);
      params.set("sortOrder", next.sortOrder);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const selectedSourceIds: string[] = (() => {
    const sources = searchParams.getAll("source").filter((value) => value.length > 0);
    if (sources.length > 0) {
      return sources;
    }
    const rawUploaderId = searchParams.get("uploaderId");
    if (!isAlbumUploaderId(rawUploaderId)) {
      return [];
    }
    const deviceKey = searchParams.get("deviceKey");
    return [deviceKey != null ? `${rawUploaderId}:${deviceKey}` : `${rawUploaderId}:`];
  })();

  const setSelectedSourceIds = useCallback(
    (next: string[]) => {
      const params = new URLSearchParams(searchParams);
      params.delete("source");
      params.delete("uploaderId");
      params.delete("deviceKey");
      next.forEach((source) => params.append("source", source));
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const sortSelectValue = `${sort.sortBy}:${sort.sortOrder}`;
  const sortOptions: Array<{ value: string; label: string; sort: MediaSortOptions }> = [
    { value: "takenAt:asc", label: "Taken At Date (old to new)", sort: { sortBy: "takenAt", sortOrder: "asc" } },
    { value: "takenAt:desc", label: "Taken At Date (new to old)", sort: { sortBy: "takenAt", sortOrder: "desc" } },
    { value: "uploadedAt:asc", label: "Upload Date (old to new)", sort: { sortBy: "uploadedAt", sortOrder: "asc" } },
    { value: "uploadedAt:desc", label: "Upload Date (new to old)", sort: { sortBy: "uploadedAt", sortOrder: "desc" } },
    { value: "name:asc", label: "Name A-Z", sort: { sortBy: "name", sortOrder: "asc" } },
    { value: "name:desc", label: "Name Z-A", sort: { sortBy: "name", sortOrder: "desc" } },
  ];
  const activeSortLabel = sortOptions.find((option) => option.value === sortSelectValue)?.label ?? "Sort";

  const handleBack = useCallback(() => {
    if (onBackProp) {
      onBackProp();
      return;
    }
    navigate("/");
  }, [onBackProp, navigate]);

  const {
    media,
    hasMedia,
    isLoading: mediaLoading,
    isDeleting,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error: mediaError,
    deleteMedia,
    refreshMedia,
    clearError,
    filteredTotal,
    loadedMediaCount,
  } = useMedia(album.ownerId, album.id, sort, selectedSourceIds);
  const isUploaderFilterActive = selectedSourceIds.length > 0;
  const visibleMediaCount =
    mediaLoading && media.length === 0
      ? isUploaderFilterActive
        ? (filteredTotal ?? 0)
        : album.mediaCount
      : media.length;
  const { uploaders, isSuccess: uploadersLoaded } = useAlbumUploaders(album.ownerId, album.id, {
    enabled: isFilterMenuOpen || selectedSourceIds.length > 0,
  });

  useEffect(() => {
    const openedAt = performance.now();
    albumDebug("album.open", {
      albumId: album.id,
      albumMediaCount: album.mediaCount,
      sortBy: sort.sortBy,
      sortOrder: sort.sortOrder,
    });
    return () => {
      albumDebug("album.close", {
        albumId: album.id,
        lifetimeMs: Number((performance.now() - openedAt).toFixed(1)),
      });
    };
  }, [album.id, album.mediaCount, sort.sortBy, sort.sortOrder]);

  useEffect(() => {
    albumDebug("album.media state", {
      albumId: album.id,
      mediaLoading,
      media: media.length,
      loadedMediaCount,
      visibleMediaCount,
      hasNextPage,
      isFetchingNextPage,
    });
  }, [
    album.id,
    hasNextPage,
    isFetchingNextPage,
    loadedMediaCount,
    media.length,
    mediaLoading,
    visibleMediaCount,
  ]);
  const activeFilterLabel = albumMediaUploaderFilterLabel(uploaders, selectedSourceIds, user?.id);
  const filterOptions: Array<{ value: string | null; label: string }> = [
    { value: null, label: "All uploaders" },
    ...uploaders.map((uploader) => ({
      value: uploader.id,
      label: albumMediaUploaderLabel(uploader, user?.id),
    })),
  ];

  // const { comments } = useAlbumComments(album.ownerId, album.id);

  const {
    likes,
    // likesCount,
    isLikedByCurrentUser,
    isLoading: likesLoading,
    isToggling: isLikeToggling,
    toggleLike,
    error: likesError,
    clearError: clearLikesError,
  } = useAlbumLikes(album.ownerId, album.id, user?.id);

  const {
    links,
    isLoading: linksLoading,
    isCreating: isCreatingLink,
    isDeleting: isDeletingLink,
    error: linksError,
    createLink,
    deleteLink,
    clearError: clearLinksError,
  } = useAlbumLinks(album.ownerId, album.id);

  const handleUploadClick = useCallback(() => {
    openPicker();
  }, [openPicker]);

  const handleMediaDelete = useCallback(
    async (mediaItem: { id: string; userId: string }) => {
      if (!canDeleteMedia(mediaItem)) {
        return;
      }
      const success = await deleteMedia(mediaItem.id);
      if (success) {
        // Media list will be updated automatically by the hook
      }
    },
    [canDeleteMedia, deleteMedia],
  );

  const deletableSelectedIds = useMemo(() => {
    const mediaById = new Map(media.map((item) => [item.id, item]));
    return Array.from(selectedMediaIds).filter((id) => {
      const item = mediaById.get(id);
      return item !== undefined && canDeleteMedia(item);
    });
  }, [canDeleteMedia, media, selectedMediaIds]);

  const handleRefresh = useCallback(async () => {
    // Clear any stale error state first.
    clearError();

    // Important: MediaGallery uses `album.mediaCount` to determine how many slots to render.
    // If new items were uploaded elsewhere, we must refetch album details so `mediaCount` updates.
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: albumKeys.detail(album.ownerId, album.id) }),
      queryClient.invalidateQueries({ queryKey: albumKeys.list(album.ownerId) }),
    ]);

    // Refetch the currently selected media list/sort.
    await refreshMedia();
  }, [album.id, album.ownerId, clearError, queryClient, refreshMedia]);

  const loadLatestMemoryVideo = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!isMemoriesVideoEnabled) {
        setMemoryVideoJob(null);
        setMemoryVideoError(null);
        return null;
      }
      if (!canRenameAlbum) {
        setMemoryVideoJob(null);
        setMemoryVideoError(null);
        return null;
      }

      if (!silent) {
        setIsMemoryVideoLoading(true);
      }

      try {
        const latest = await albumService.getAlbumMemoryVideo(album.ownerId, album.id);
        setMemoryVideoJob(latest);
        setMemoryVideoError(null);
        return latest;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Failed to load memories video";
        setMemoryVideoError(errorMessage);
        logError(error as Error);
        return null;
      } finally {
        if (!silent) {
          setIsMemoryVideoLoading(false);
        }
      }
    },
    [album.id, album.ownerId, canRenameAlbum, isMemoriesVideoEnabled]
  );

  const handleMemoryVideoClick = useCallback(async () => {
    if (!isMemoriesVideoEnabled) return;
    if (!canRenameAlbum) return;

    if (memoryVideoJob?.status === "done" && memoryVideoJob.url) {
      window.open(memoryVideoJob.url, "_blank", "noopener,noreferrer");
      return;
    }

    if (isCreatingMemoryVideo || isMemoryVideoProcessing) return;
    setSelectedMemoryVideoTemplate(memoryVideoJob?.template ?? "calm");
    setIsMemoryVideoModalOpen(true);
  }, [
    canRenameAlbum,
    isMemoriesVideoEnabled,
    isCreatingMemoryVideo,
    isMemoryVideoProcessing,
    memoryVideoJob,
  ]);

  const closeMemoryVideoModal = useCallback(() => {
    if (isCreatingMemoryVideo) return;
    setIsMemoryVideoModalOpen(false);
  }, [isCreatingMemoryVideo]);

  const handleCreateMemoryVideo = useCallback(async () => {
    if (!isMemoriesVideoEnabled) return;
    if (!canRenameAlbum || isCreatingMemoryVideo || isMemoryVideoProcessing) return;

    setIsCreatingMemoryVideo(true);
    setMemoryVideoError(null);

    try {
      const job = await albumService.createAlbumMemoryVideo(album.ownerId, album.id, {
        template: selectedMemoryVideoTemplate,
      });
      setMemoryVideoJob(job);
      setIsMemoryVideoModalOpen(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to create memories video";
      setMemoryVideoError(errorMessage);
      logError(error as Error);
    } finally {
      setIsCreatingMemoryVideo(false);
    }
  }, [
    album.id,
    album.ownerId,
    canRenameAlbum,
    isMemoriesVideoEnabled,
    isCreatingMemoryVideo,
    isMemoryVideoProcessing,
    selectedMemoryVideoTemplate,
  ]);

  // const handleCommentsClick = useCallback(() => {
  //   setShowComments(true);
  // }, []);

  const handleCommentsClose = useCallback(() => {
    setShowComments(false);
  }, []);

  // const handleLikesClick = useCallback(() => {
  //   setShowLikes(true);
  // }, []);

  const handleLikesClose = useCallback(() => {
    setShowLikes(false);
  }, []);

  const handleImageViewerStateChange = useCallback((isOpen: boolean) => {
    setIsImageViewerOpen(isOpen);
  }, []);

  const handleShareClick = useCallback(() => {
    setShowShareLinks(true);
  }, []);

  const handleShareClose = useCallback(() => {
    setShowShareLinks(false);
  }, []);

  const handleCreateShareLink = useCallback(async (permission: "read_only" | "upload") => {
    await createLink(permission);
  }, [createLink]);

  const handleDeleteShareLink = useCallback(async (linkId: string) => {
    await deleteLink(linkId);
  }, [deleteLink]);

  const handleDownloadAlbum = useCallback(async () => {
    if (isDownloading) return;
    
    setIsDownloading(true);
    setDownloadError(null);
    
    try {
      await albumService.downloadAlbumZip(album.ownerId, album.id);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to download album";
      setDownloadError(errorMessage);
      logError(error as Error);
    } finally {
      setIsDownloading(false);
    }
  }, [album.ownerId, album.id, isDownloading]);

  const clearDownloadError = useCallback(() => {
    setDownloadError(null);
  }, []);

  const toggleSelectionMode = useCallback(() => {
    setDownloadSelectionError(null);
    setDeleteSelectionError(null);
    setIsSelectionMode((prev) => {
      const next = !prev;
      if (!next) {
        setSelectedMediaIds(new Set());
        setIsActionsMenuOpen(false);
        lastSelectedMediaIdRef.current = null;
      }
      return next;
    });
  }, []);

  const setSelectionModeEnabled = useCallback((enabled: boolean) => {
    setDownloadSelectionError(null);
    setDeleteSelectionError(null);
    setIsSelectionMode(enabled);

    if (!enabled) {
      setSelectedMediaIds(new Set());
      setIsActionsMenuOpen(false);
      lastSelectedMediaIdRef.current = null;
    }
  }, []);

  const toggleMediaSelection = useCallback((
    mediaId: string,
    options?: { rangeSelect?: boolean; selected?: boolean }
  ) => {
    const anchorMediaId = lastSelectedMediaIdRef.current;

    setSelectedMediaIds((prev) => {
      const next = new Set(prev);

      if (options?.rangeSelect && anchorMediaId && anchorMediaId !== mediaId) {
        const anchorIndex = media.findIndex((item) => item.id === anchorMediaId);
        const currentIndex = media.findIndex((item) => item.id === mediaId);

        if (anchorIndex !== -1 && currentIndex !== -1) {
          const [startIndex, endIndex] = anchorIndex < currentIndex
            ? [anchorIndex, currentIndex]
            : [currentIndex, anchorIndex];

          media.slice(startIndex, endIndex + 1).forEach((item) => {
            next.add(item.id);
          });

          return next;
        }
      }

      if (typeof options?.selected === "boolean") {
        if (options.selected) {
          next.add(mediaId);
        } else {
          next.delete(mediaId);
        }
      } else if (next.has(mediaId)) {
        next.delete(mediaId);
      } else {
        next.add(mediaId);
      }

      return next;
    });

    lastSelectedMediaIdRef.current = mediaId;
  }, [media]);

  const applyMediaSelectionRange = useCallback((
    anchorMediaId: string,
    currentMediaId: string,
    selected: boolean,
    baseSelectedMediaIds: Set<string>
  ) => {
    const anchorIndex = media.findIndex((item) => item.id === anchorMediaId);
    const currentIndex = media.findIndex((item) => item.id === currentMediaId);

    if (anchorIndex === -1 || currentIndex === -1) return;

    const [startIndex, endIndex] = anchorIndex < currentIndex
      ? [anchorIndex, currentIndex]
      : [currentIndex, anchorIndex];

    const next = new Set(baseSelectedMediaIds);
    media.slice(startIndex, endIndex + 1).forEach((item) => {
      if (selected) {
        next.add(item.id);
      } else {
        next.delete(item.id);
      }
    });

    setSelectedMediaIds(next);
    lastSelectedMediaIdRef.current = currentMediaId;
  }, [media]);

  const clearSelection = useCallback(() => {
    setSelectedMediaIds(new Set());
    lastSelectedMediaIdRef.current = null;
  }, []);

  const selectAllLoaded = useCallback(() => {
    setSelectedMediaIds(new Set(media.map((m) => m.id)));
    lastSelectedMediaIdRef.current = null;
  }, [media]);

  const handleDownloadSelection = useCallback(async () => {
    if (isDownloadingSelection) return;
    if (selectedMediaIds.size === 0) return;

    setIsDownloadingSelection(true);
    setDownloadSelectionError(null);

    try {
      const selected = media.filter((m) => selectedMediaIds.has(m.id));
      if (selected.length === 0) return;

      const getFilenameFromContentDisposition = (contentDisposition: string | null): string | null => {
        if (!contentDisposition) return null;
        const filenameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-8'')?([^;\r\n"']*)['"]?/i);
        if (filenameMatch && filenameMatch[1]) {
          return decodeURIComponent(filenameMatch[1]);
        }
        return null;
      };

      const getMimeTypeFromFilename = (filename: string): string | undefined => {
        const ext = filename.split(".").pop()?.toLowerCase();
        if (!ext) return undefined;
        switch (ext) {
          case "jpg":
          case "jpeg":
            return "image/jpeg";
          case "png":
            return "image/png";
          case "gif":
            return "image/gif";
          case "webp":
            return "image/webp";
          case "heic":
            return "image/heic";
          case "heif":
            return "image/heif";
          case "mp4":
            return "video/mp4";
          case "mov":
            return "video/quicktime";
          case "m4v":
            return "video/x-m4v";
          case "avi":
            return "video/x-msvideo";
          case "mkv":
            return "video/x-matroska";
          default:
            return undefined;
        }
      };

      const downloadBlob = (blob: Blob, filename: string) => {
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      };

      const failures: string[] = [];

      // On mobile (iOS/Android), use Web Share with files when available.
      // This routes the user through the native share sheet where "Save Image/Video" (iOS Photos)
      // or a gallery target (Android) can place media into the right app.
      const isMobileDevice = typeof navigator !== "undefined" && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const supportsWebShareFiles =
        typeof navigator !== "undefined" &&
        typeof navigator.share === "function" &&
        typeof navigator.canShare === "function" &&
        typeof File === "function";

      const MAX_WEB_SHARE_TOTAL_BYTES = 250 * 1024 * 1024; // best-effort guardrail (platforms vary).

      const shouldTryWebShare = isMobileDevice && supportsWebShareFiles && selected.length <= MAX_NATIVE_SAVE_FILES;

      if (shouldTryWebShare) {
        const shareFiles: File[] = [];
        let totalBytes = 0;

        for (const mediaItem of selected) {
          const url = mediaItem.originalUrl || mediaItem.url;
          if (!url) {
            failures.push(mediaItem.filename || mediaItem.id);
            continue;
          }

          try {
            const response = await fetchAuthenticatedRaw(url, { method: "GET" });
            const contentDisposition = response.headers.get("Content-Disposition");
            const filename = getFilenameFromContentDisposition(contentDisposition) || mediaItem.filename || "download";
            const blob = await response.blob();

            const nextTotalBytes = totalBytes + blob.size;
            if (nextTotalBytes > MAX_WEB_SHARE_TOTAL_BYTES) {
              // Too large to reliably share on mobile; fall back to normal downloads for all selected files.
              shareFiles.length = 0;
              break;
            }
            totalBytes = nextTotalBytes;

            const mimeType = blob.type || getMimeTypeFromFilename(filename);
            const file = new File([blob], filename, mimeType ? { type: mimeType } : undefined);
            shareFiles.push(file);
          } catch (error) {
            failures.push(mediaItem.filename || mediaItem.id);
            logError(error as Error);
          }
        }

        if (shareFiles.length > 0 && navigator.canShare({ files: shareFiles })) {
          try {
            await navigator.share({ files: shareFiles, title: album.name });
            return;
          } catch (error) {
            // User cancel shouldn't be treated as a failure.
            if (error instanceof DOMException && error.name === "AbortError") return;
            logError(error as Error);
            // Fall through to the normal download flow.
          }
        }
      }

      // Default behavior: download sequentially to reduce memory pressure and avoid spamming the browser too hard.
      for (const mediaItem of selected) {
        const url = mediaItem.originalUrl || mediaItem.url;
        if (!url) {
          failures.push(mediaItem.filename || mediaItem.id);
          continue;
        }

        try {
          const response = await fetchAuthenticatedRaw(url, { method: "GET" });
          const contentDisposition = response.headers.get("Content-Disposition");
          const filename = getFilenameFromContentDisposition(contentDisposition) || mediaItem.filename || "download";
          const blob = await response.blob();
          downloadBlob(blob, filename);

          // Tiny delay helps some browsers reliably queue multiple downloads from a single click.
          await new Promise((resolve) => setTimeout(resolve, 150));
        } catch (error) {
          failures.push(mediaItem.filename || mediaItem.id);
          logError(error as Error);
        }
      }

      if (failures.length > 0) {
        setDownloadSelectionError(
          `Failed to download ${failures.length} of ${selected.length} files`
        );
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to download selection";
      setDownloadSelectionError(errorMessage);
      logError(error as Error);
    } finally {
      setIsDownloadingSelection(false);
    }
  }, [MAX_NATIVE_SAVE_FILES, album.name, isDownloadingSelection, media, selectedMediaIds]);

  const clearDownloadSelectionError = useCallback(() => {
    setDownloadSelectionError(null);
  }, []);

  const clearDeleteSelectionError = useCallback(() => {
    setDeleteSelectionError(null);
  }, []);

  const openRenameModal = useCallback(() => {
    if (!canRenameAlbum) return;
    setRenameValue(album.name);
    setRenameError(null);
    setIsRenameModalOpen(true);
  }, [album.name, canRenameAlbum]);

  const closeRenameModal = useCallback(() => {
    if (isRenamingAlbum) return;
    setIsRenameModalOpen(false);
    setRenameError(null);
    setRenameValue(album.name);
  }, [album.name, isRenamingAlbum]);

  const handleRenameSubmit = useCallback(async () => {
    if (!canRenameAlbum || isRenamingAlbum) return;

    const trimmedName = renameValue.trim();
    if (!trimmedName) {
      setRenameError("Album name is required");
      return;
    }
    if (trimmedName.length < 2) {
      setRenameError("Album name must be at least 2 characters");
      return;
    }
    if (trimmedName.length > 100) {
      setRenameError("Album name must be less than 100 characters");
      return;
    }
    if (trimmedName === album.name) {
      setIsRenameModalOpen(false);
      return;
    }

    setIsRenamingAlbum(true);
    setRenameError(null);
    try {
      const updatedAlbum = await albumService.updateAlbum(album.ownerId, album.id, {
        name: trimmedName,
        version: album.version,
      });
      queryClient.setQueryData(albumKeys.detail(album.ownerId, album.id), updatedAlbum);
      queryClient.setQueryData<Album[] | undefined>(albumKeys.list(album.ownerId), (existing) => {
        if (!existing) return existing;
        return existing.map((existingAlbum) => (existingAlbum.id === updatedAlbum.id ? updatedAlbum : existingAlbum));
      });
      setIsRenameModalOpen(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to rename album";
      setRenameError(errorMessage);
      logError(error as Error);
    } finally {
      setIsRenamingAlbum(false);
    }
  }, [album.id, album.name, album.ownerId, album.version, canRenameAlbum, isRenamingAlbum, queryClient, renameValue]);

  const openDeleteSelectionConfirm = useCallback(() => {
    if (!canDelete) return;
    if (deletableSelectedIds.length === 0) return;
    setIsActionsMenuOpen(false);
    setIsDeleteSelectionModalOpen(true);
  }, [canDelete, deletableSelectedIds.length]);

  const closeDeleteSelectionConfirm = useCallback(() => {
    if (isDeletingSelection) return;
    setIsDeleteSelectionModalOpen(false);
  }, [isDeletingSelection]);

  const handleConfirmDeleteSelection = useCallback(async () => {
    if (!canDelete) return;
    if (isDeletingSelection) return;
    if (deletableSelectedIds.length === 0) return;

    setIsDeletingSelection(true);
    setDeleteSelectionError(null);

    const ids = deletableSelectedIds;
    const failedIds: string[] = [];

    try {
      for (const mediaId of ids) {
        try {
          await deleteMedia(mediaId);
        } catch (error) {
          failedIds.push(mediaId);
          logError(error as Error);
        }
      }

      if (failedIds.length > 0) {
        setDeleteSelectionError(`Failed to delete ${failedIds.length} of ${ids.length} files`);
      }

      // Keep only failed selections (if any), otherwise clear selection.
      setSelectedMediaIds(new Set(failedIds));
      setIsDeleteSelectionModalOpen(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to delete selection";
      setDeleteSelectionError(errorMessage);
      logError(error as Error);
    } finally {
      setIsDeletingSelection(false);
    }
  }, [canDelete, deletableSelectedIds, deleteMedia, isDeletingSelection]);

  useEffect(() => {
    if (!isMemoriesVideoEnabled || !canRenameAlbum) {
      setMemoryVideoJob(null);
      setMemoryVideoError(null);
      setIsMemoryVideoLoading(false);
      setIsCreatingMemoryVideo(false);
      setIsMemoryVideoModalOpen(false);
      return;
    }

    void loadLatestMemoryVideo();
  }, [canRenameAlbum, isMemoriesVideoEnabled, loadLatestMemoryVideo]);

  useEffect(() => {
    if (!isMemoriesVideoEnabled || !canRenameAlbum || !isMemoryVideoProcessing) return;

    const intervalId = window.setInterval(() => {
      void loadLatestMemoryVideo({ silent: true });
    }, 5000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [canRenameAlbum, isMemoriesVideoEnabled, isMemoryVideoProcessing, loadLatestMemoryVideo]);

  useEffect(() => {
    if (!isActionsMenuOpen) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (actionsMenuRef.current && actionsMenuRef.current.contains(target)) return;
      setIsActionsMenuOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [isActionsMenuOpen]);

  useEffect(() => {
    if (!uploadersLoaded || selectedSourceIds.length === 0) return;
    const validIds = selectedSourceIds.filter((id) => uploaders.some((uploader) => uploader.id === id));
    if (validIds.length !== selectedSourceIds.length) {
      setSelectedSourceIds(validIds);
    }
  }, [selectedSourceIds, uploaders, uploadersLoaded, setSelectedSourceIds]);

  const openFilterMenu = useCallback(() => {
    setIsSortMenuOpen(false);
    setIsActionsMenuOpen(false);
    if (window.matchMedia("(max-width: 639px)").matches) {
      setIsMobileMenuOpen(true);
    }
    setIsFilterMenuOpen(true);
  }, []);

  useEffect(() => {
    if (!isSortMenuOpen) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (sortMenuRef.current && sortMenuRef.current.contains(target)) return;
      setIsSortMenuOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsSortMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSortMenuOpen]);

  useEffect(() => {
    if (!isFilterMenuOpen) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (filterMenuRef.current && filterMenuRef.current.contains(target)) return;
      setIsFilterMenuOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsFilterMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFilterMenuOpen]);

  // Track header height so content can sit below the fixed header.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const readHeaderHeight = () => Math.ceil(el.getBoundingClientRect().height);
    const observer = new ResizeObserver(() => {
      const measured = readHeaderHeight();
      setHeaderHeight((prev) => (measured > prev ? measured : prev));
    });
    observer.observe(el);
    const initialMeasured = readHeaderHeight();
    setHeaderHeight((prev) => (initialMeasured > prev ? initialMeasured : prev));
    return () => observer.disconnect();
  }, []);

  // Compact the top bar while scrolling down to prioritize gallery space.
  useEffect(() => {
    const compactOnThreshold = 96;
    const compactOffThreshold = 48;
    let frameId: number | null = null;

    const updateCompactState = () => {
      const scrollY = window.scrollY;
      setIsScrollCompactHeader((prev) => {
        if (prev) return scrollY > compactOffThreshold;
        return scrollY > compactOnThreshold;
      });
      frameId = null;
    };

    const handleScroll = () => {
      if (frameId !== null) return;
      frameId = window.requestAnimationFrame(updateCompactState);
    };

    updateCompactState();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (frameId !== null) window.cancelAnimationFrame(frameId);
    };
  }, []);

  const isCompactHeader = isScrollCompactHeader;

  // Note: No need to refresh media after upload since useMedia hook already adds uploaded items to state

  return (
    <div data-section="album-detail-page" className="min-h-screen relative overflow-hidden">
      <div data-section="album-detail-content-layer" className="relative z-10">
        {/* Header */}
        <header ref={headerRef} className="fixed top-0 left-0 right-0 z-50 bg-black/20 backdrop-blur-xl border-b border-white/10">
          <div
            data-section="album-detail-header-shell"
            className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-[padding] duration-300 ${
              isCompactHeader ? "py-2" : "py-3 sm:py-4"
            }`}
          >
            <div
              data-section="album-detail-header-layout"
              className="flex items-center justify-between gap-3"
            >
              <div data-section="album-detail-header-title-group" className="flex min-w-0 flex-1 items-center gap-1">
                <button
                  type="button"
                  onClick={handleBack}
                  className={`group inline-flex flex-shrink-0 items-center justify-center text-[var(--primary-blue)] hover:text-[var(--primary-blue-light)] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--focus-ring)] rounded-lg ${
                    isCompactHeader ? "h-9 w-9" : "h-10 w-10"
                  }`}
                  title="Back"
                  aria-label="Back"
                >
                  <svg
                    className={`group-hover:-translate-x-0.5 transition-transform duration-200 ${
                      isCompactHeader ? "h-7 w-7" : "h-8 w-8"
                    }`}
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={48}
                    viewBox="0 0 512 512"
                    aria-hidden="true"
                  >
                    <path d="M328 112L184 256l144 144" />
                  </svg>
                </button>

                <div className="min-w-0 flex-1">
                  <h1
                    className={`font-bold text-white leading-tight transition-[font-size] duration-200 ${
                      isCompactHeader ? "text-lg sm:text-xl" : "text-xl sm:text-2xl"
                    }`}
                  >
                    {canRenameAlbum ? (
                      <button
                        type="button"
                        onClick={openRenameModal}
                        className="group -mx-1 block w-full max-w-full rounded-lg px-1 text-left text-white transition-colors duration-200 hover:text-[rgb(var(--primary-green-rgb))] focus:outline-none focus:ring-2 focus:ring-[rgba(var(--primary-blue-rgb),0.35)]"
                        title="Rename album"
                      >
                        <span className="line-clamp-2 break-words">{album.name}</span>
                      </button>
                    ) : (
                      <span className="line-clamp-2 break-words">{album.name}</span>
                    )}
                  </h1>
                  {album.description && (
                    <p className="text-gray-300 text-xs sm:text-sm truncate">{album.description}</p>
                  )}
                </div>
              </div>

              {/* Mobile-only: compact ⋯ button (hidden on sm+) */}
              <div className="flex items-center gap-2 flex-shrink-0 sm:hidden">
                <button
                  onClick={() => setIsMobileMenuOpen(true)}
                  className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[rgba(var(--primary-blue-rgb),0.35)]"
                  title="More actions"
                  aria-label="More actions"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <circle cx="5" cy="12" r="1.5" />
                    <circle cx="12" cy="12" r="1.5" />
                    <circle cx="19" cy="12" r="1.5" />
                  </svg>
                </button>
              </div>

              {/* Desktop-only controls (hidden below sm) */}
              <div className="hidden sm:block sm:ml-4 sm:flex-shrink-0">
              <div
                data-section="album-detail-header-controls"
                className="grid"
              >
                <div className="overflow-visible min-h-0">
                  <div data-section="album-detail-header-controls-grid" className="grid w-full gap-2 sm:w-auto sm:min-w-[18rem]">
                  <div data-section="album-detail-primary-actions-row" className="flex flex-wrap items-center justify-start gap-2 sm:justify-end">
                    <div className="relative flex items-center gap-2" ref={sortMenuRef}>
                      <button
                        type="button"
                        onClick={() => {
                          setIsFilterMenuOpen(false);
                          setIsSortMenuOpen((prev) => !prev);
                        }}
                        className="p-2 text-gray-300 hover:text-white bg-white/10 hover:bg-white/20 backdrop-blur-xl border border-white/20 rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[rgba(var(--primary-blue-rgb),0.35)]"
                        title={`Sort Media: ${activeSortLabel}`}
                        aria-haspopup="menu"
                        aria-expanded={isSortMenuOpen}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 6h9m-9 6h8m-8 6h7M3 18h7m6-13v14m0 0l3-3m-3 3l-3-3"
                          />
                        </svg>
                      </button>

                      {isSortMenuOpen && (
                        <div
                          role="menu"
                          aria-label="Sort options"
                          className="absolute left-0 top-12 z-50 w-72 bg-slate-900/90 backdrop-blur-xl border border-white/15 rounded-2xl shadow-2xl overflow-hidden"
                        >
                          {sortOptions.map((option, index) => {
                            const isActive = option.value === sortSelectValue;
                            return (
                              <button
                                key={option.value}
                                type="button"
                                role="menuitemradio"
                                aria-checked={isActive}
                                onClick={() => {
                                  setSort(option.sort);
                                  setIsSortMenuOpen(false);
                                }}
                                className={[
                                  "w-full px-4 py-3 text-left transition-all duration-200 flex items-center gap-3",
                                  index > 0 ? "border-t border-white/10" : "",
                                  isActive
                                    ? "text-white bg-white/10"
                                    : "text-gray-200 hover:text-white hover:bg-white/10",
                                ].join(" ")}
                              >
                                <span className="w-5 h-5 flex items-center justify-center">
                                  {isActive && (
                                    <svg
                                      className="w-5 h-5 text-[rgba(var(--primary-green-rgb),0.85)]"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </span>
                                <span>{option.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="relative flex items-center gap-2" ref={filterMenuRef}>
                      <button
                        type="button"
                        onClick={() => {
                          setIsSortMenuOpen(false);
                          setIsFilterMenuOpen((prev) => !prev);
                        }}
                        className="p-2 text-gray-300 hover:text-white bg-white/10 hover:bg-white/20 backdrop-blur-xl border border-white/20 rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[rgba(var(--primary-blue-rgb),0.35)]"
                        title={`Filter Media: ${activeFilterLabel}`}
                        aria-haspopup="menu"
                        aria-expanded={isFilterMenuOpen}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 4h18l-7 8v6l-4 2v-8L3 4z"
                          />
                        </svg>
                      </button>

                      {isFilterMenuOpen && (
                        <div
                          role="menu"
                          aria-label="Filter options"
                          className="absolute left-0 top-12 z-50 w-72 max-h-80 overflow-y-auto bg-slate-900/90 backdrop-blur-xl border border-white/15 rounded-2xl shadow-2xl"
                        >
                          {isFilterMenuOpen && !uploadersLoaded ? (
                            <div className="px-4 py-3 text-sm text-gray-300">Loading uploaders...</div>
                          ) : (
                            filterOptions.map((option, index) => {
                            const isActive = option.value
                              ? selectedSourceIds.includes(option.value)
                              : selectedSourceIds.length === 0;
                            return (
                              <button
                                key={option.value ?? "all"}
                                type="button"
                                role="menuitemcheckbox"
                                aria-checked={isActive}
                                onClick={() => {
                                  setSelectedSourceIds(
                                    toggleAlbumUploaderFilter(
                                      selectedSourceIds,
                                      option.value,
                                      uploaders.map((uploader) => uploader.id),
                                    ),
                                  );
                                }}
                                className={[
                                  "w-full px-4 py-3 text-left transition-all duration-200 flex items-center gap-3",
                                  index > 0 ? "border-t border-white/10" : "",
                                  isActive
                                    ? "text-white bg-white/10"
                                    : "text-gray-200 hover:text-white hover:bg-white/10",
                                ].join(" ")}
                              >
                                <span className="w-5 h-5 flex items-center justify-center">
                                  {isActive && (
                                    <svg
                                      className="w-5 h-5 text-[rgba(var(--primary-green-rgb),0.85)]"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </span>
                                <span className="truncate">{option.label}</span>
                              </button>
                            );
                          })
                          )}
                        </div>
                      )}
                    </div>

                    <button
                      onClick={handleShareClick}
                      className="p-2 text-gray-300 hover:text-white bg-white/10 hover:bg-white/20 backdrop-blur-xl border border-white/20 rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[rgba(var(--primary-blue-rgb),0.35)]"
                      title="Share Album"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                      </svg>
                    </button>

                    <button
                      onClick={toggleSelectionMode}
                      className={[
                        "p-2 bg-white/10 hover:bg-white/20 backdrop-blur-xl border rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[rgba(var(--primary-blue-rgb),0.35)]",
                        isSelectionMode ? "text-white border-purple-400/40" : "text-gray-300 border-white/20 hover:text-white",
                      ].join(" ")}
                      title={isSelectionMode ? "Exit selection mode" : "Select items"}
                    >
                      {isSelectionMode ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="2 -2 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 11l3 3L22 4" />
                        </svg>
                      )}
                    </button>

                    <div
                      className={isCompactHeader ? "pointer-events-none" : undefined}
                      style={{
                        overflow: "hidden",
                        opacity: isCompactHeader ? 0 : 1,
                        maxWidth: isCompactHeader ? "0px" : "40px",
                        transition: "opacity 220ms ease-out, max-width 220ms ease-out",
                        willChange: "opacity, max-width",
                      }}
                      aria-hidden={isCompactHeader}
                    >
                      <button
                        onClick={handleRefresh}
                        disabled={mediaLoading || isCompactHeader}
                        className="w-10 h-10 p-2 text-gray-300 hover:text-white bg-white/10 hover:bg-white/20 backdrop-blur-xl border border-white/20 rounded-xl transition-all duration-300 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[rgba(var(--primary-blue-rgb),0.35)]"
                        title="Refresh"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      </button>
                    </div>

                    <div
                      className={isCompactHeader ? "pointer-events-none" : undefined}
                      style={{
                        overflow: "hidden",
                        opacity: isCompactHeader ? 0 : 1,
                        maxWidth: isCompactHeader ? "0px" : "40px",
                        transition: "opacity 220ms ease-out, max-width 220ms ease-out",
                        willChange: "opacity, max-width",
                      }}
                      aria-hidden={isCompactHeader}
                    >
                      <button
                        onClick={handleDownloadAlbum}
                        disabled={isDownloading || isCompactHeader}
                        className="w-10 h-10 p-2 text-gray-300 hover:text-white bg-white/10 hover:bg-white/20 backdrop-blur-xl border border-white/20 rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[rgba(var(--primary-blue-rgb),0.35)] disabled:opacity-50 disabled:cursor-not-allowed"
                        title={isDownloading ? "Downloading..." : "Download Album as ZIP"}
                      >
                        {isDownloading ? (
                          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        )}
                      </button>
                    </div>

                    {isMemoriesVideoEnabled && canRenameAlbum && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleMemoryVideoClick}
                          disabled={isMemoryVideoLoading || isCreatingMemoryVideo || isMemoryVideoProcessing}
                          className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-[rgba(var(--primary-green-rgb),0.3)] bg-[rgba(var(--primary-green-rgb),0.15)] px-3 text-sm font-medium text-white transition-all duration-300 hover:bg-[rgba(var(--primary-green-rgb),0.25)] focus:outline-none focus:ring-2 focus:ring-[rgba(var(--primary-blue-rgb),0.35)] disabled:cursor-not-allowed disabled:opacity-60"
                          title={memoryVideoButtonLabel}
                        >
                          {isMemoryVideoLoading || isCreatingMemoryVideo || isMemoryVideoProcessing ? (
                            <div className="h-4 w-4 rounded-full border-2 border-white/60 border-t-transparent animate-spin" />
                          ) : memoryVideoJob?.status === "done" && memoryVideoJob.url ? (
                            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M6 4.5A1.5 1.5 0 0 1 8.277 3.22l7 4.5a1.5 1.5 0 0 1 0 2.56l-7 4.5A1.5 1.5 0 0 1 6 13.5v-9Z" />
                            </svg>
                          ) : (
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14m-6 4h2a2 2 0 002-2V8a2 2 0 00-2-2H9a2 2 0 00-2 2v8a2 2 0 002 2z"
                              />
                            </svg>
                          )}
                          <span className="hidden md:inline">{memoryVideoButtonLabel}</span>
                          <span className="md:hidden">{compactMemoryVideoLabel}</span>
                        </button>

                        {memoryVideoJob && !isMemoryVideoProcessing && (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedMemoryVideoTemplate(memoryVideoJob.template ?? "calm");
                              setIsMemoryVideoModalOpen(true);
                            }}
                            disabled={isCreatingMemoryVideo}
                            className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/10 px-3 text-sm font-medium text-gray-200 transition-all duration-300 hover:bg-white/15 hover:text-white focus:outline-none focus:ring-2 focus:ring-[rgba(var(--primary-blue-rgb),0.35)] disabled:cursor-not-allowed disabled:opacity-60"
                            title="Regenerate memories video"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            <span className="hidden lg:inline">Regenerate</span>
                          </button>
                        )}
                      </div>
                    )}

                    {canUpload && (
                      <button
                        onClick={handleUploadClick}
                        disabled={isPreparingUpload}
                        className="group ml-auto inline-flex h-9 items-center justify-center gap-2 px-3 py-2 text-sm text-white rounded-xl bg-[linear-gradient(30deg,var(--primary-green-dark),var(--primary-green))] hover:bg-[linear-gradient(90deg,var(--primary-green),var(--primary-green))] shadow-lg shadow-[rgba(var(--primary-green-rgb),0.30)] hover:shadow-[rgba(var(--primary-green-rgb),0.50)] hover:-translate-y-0.5 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[rgba(var(--primary-green-rgb),0.10)] disabled:opacity-60 sm:w-auto"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {isMemoriesVideoEnabled && canRenameAlbum && memoryVideoStatusMessage && (
                    <div
                      data-section="album-detail-memory-video-status"
                      className={`min-h-[1.25rem] text-xs sm:text-sm sm:text-right ${memoryVideoStatusClassName}`}
                    >
                      {memoryVideoStatusMessage}
                    </div>
                  )}

                  <div
                    data-section="album-detail-reactions-row"
                    className={isCompactHeader ? "pointer-events-none" : undefined}
                    style={{
                      opacity: isCompactHeader ? 0 : 1,
                      display: "grid",
                      gridTemplateRows: isCompactHeader ? "0fr" : "1fr",
                      transition: "opacity 220ms ease-out, grid-template-rows 220ms ease-out, margin-top 220ms ease-out",
                      marginTop: isCompactHeader ? "0px" : "2px",
                      willChange: "opacity, grid-template-rows",
                    }}
                    aria-hidden={isCompactHeader}
                  >
                    <div className="overflow-hidden min-h-0">
                      {/* <div className="flex w-full flex-wrap items-center justify-start gap-2 sm:justify-end">
                      <AlbumReactionsControls
                        className="w-full sm:flex-row"
                        likeClassName="h-9 min-w-0 sm:flex-1"
                        commentsClassName="h-9 min-w-0 sm:flex-1"
                        commentCount={comments?.length || 0}
                        onCommentsClick={handleCommentsClick}
                        likesCount={likesCount}
                        isLikedByCurrentUser={isLikedByCurrentUser}
                        onLikeToggle={toggleLike}
                        onLikesListClick={handleLikesClick}
                        isLikeToggling={isLikeToggling}
                        isLoading={likesLoading}
                        canLike={canLike}
                        canComment={canComment}
                      />
                      </div> */}
                    </div>
                  </div>
                </div>
                </div>
              </div>
              </div>
            </div>
          </div>
        </header>

        {/* Mobile bottom sheet actions menu */}
        {isMobileMenuOpen && (
          <OverlayRoot
            className="fixed inset-0 z-[55] sm:hidden overscroll-none"
            onClick={() => {
              setIsMobileMenuOpen(false);
              setIsSortMenuOpen(false);
              setIsFilterMenuOpen(false);
            }}
          >
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <div
              className="absolute bottom-0 left-0 right-0 bg-slate-950/95 backdrop-blur-xl border-t border-white/10 rounded-t-3xl shadow-2xl pb-safe"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Handle */}
              <div className="flex justify-center pt-3 pb-1">
                <div className="w-10 h-1 rounded-full bg-white/25" />
              </div>

              <div className="px-4 pb-6 pt-2 space-y-1">
                {/* Sort */}
                <div className="relative" ref={sortMenuRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setIsFilterMenuOpen(false);
                      setIsSortMenuOpen((prev) => !prev);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left text-white/90 hover:bg-white/8 transition-colors duration-200"
                  >
                    <svg className="w-5 h-5 text-white/60 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6h9m-9 6h8m-8 6h7M3 18h7m6-13v14m0 0l3-3m-3 3l-3-3" />
                    </svg>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold">Sort</div>
                      <div className="text-xs text-white/50 truncate">{activeSortLabel}</div>
                    </div>
                    <svg className={`w-4 h-4 text-white/40 transition-transform ${isSortMenuOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {isSortMenuOpen && (
                    <div className="mx-4 mb-2 bg-white/8 border border-white/10 rounded-2xl overflow-hidden">
                      {sortOptions.map((option, index) => {
                        const isActive = option.value === sortSelectValue;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => {
                              setSort(option.sort);
                              setIsSortMenuOpen(false);
                              setIsMobileMenuOpen(false);
                            }}
                            className={[
                              "w-full px-4 py-3 text-left flex items-center gap-3 transition-colors duration-200",
                              index > 0 ? "border-t border-white/8" : "",
                              isActive ? "text-white bg-white/8" : "text-white/70 hover:text-white hover:bg-white/6",
                            ].join(" ")}
                          >
                            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                              {isActive && (
                                <svg className="w-4 h-4 text-[var(--primary-green)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </span>
                            <span className="text-sm">{option.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Filter */}
                <div className="relative" ref={filterMenuRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setIsSortMenuOpen(false);
                      setIsFilterMenuOpen((prev) => !prev);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left text-white/90 hover:bg-white/8 transition-colors duration-200"
                  >
                    <svg className="w-5 h-5 text-white/60 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h18l-7 8v6l-4 2v-8L3 4z" />
                    </svg>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold">Filter</div>
                      <div className="text-xs text-white/50 truncate">{activeFilterLabel}</div>
                    </div>
                    <svg className={`w-4 h-4 text-white/40 transition-transform ${isFilterMenuOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {isFilterMenuOpen && (
                    <div className="mx-4 mb-2 max-h-64 overflow-y-auto overscroll-contain bg-white/8 border border-white/10 rounded-2xl">
                      {isFilterMenuOpen && !uploadersLoaded ? (
                        <div className="px-4 py-3 text-sm text-white/70">Loading uploaders...</div>
                      ) : (
                        filterOptions.map((option, index) => {
                        const isActive = option.value
                          ? selectedSourceIds.includes(option.value)
                          : selectedSourceIds.length === 0;
                        return (
                          <button
                            key={option.value ?? "all"}
                            type="button"
                            onClick={() => {
                              setSelectedSourceIds(
                                toggleAlbumUploaderFilter(
                                  selectedSourceIds,
                                  option.value,
                                  uploaders.map((uploader) => uploader.id),
                                ),
                              );
                            }}
                            className={[
                              "w-full px-4 py-3 text-left flex items-center gap-3 transition-colors duration-200",
                              index > 0 ? "border-t border-white/8" : "",
                              isActive ? "text-white bg-white/8" : "text-white/70 hover:text-white hover:bg-white/6",
                            ].join(" ")}
                          >
                            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                              {isActive && (
                                <svg className="w-4 h-4 text-[var(--primary-green)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </span>
                            <span className="text-sm truncate">{option.label}</span>
                          </button>
                        );
                      })
                      )}
                    </div>
                  )}
                </div>

                {/* Share */}
                <button
                  onClick={() => { handleShareClick(); setIsMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left text-white/90 hover:bg-white/8 transition-colors duration-200"
                >
                  <svg className="w-5 h-5 text-white/60 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  <span className="font-semibold">Share album</span>
                </button>

                {/* Select */}
                <button
                  onClick={() => { toggleSelectionMode(); setIsMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left text-white/90 hover:bg-white/8 transition-colors duration-200"
                >
                  <svg className="w-5 h-5 text-white/60 flex-shrink-0" fill="none" stroke="currentColor" viewBox="2 -2 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 11l3 3L22 4" />
                  </svg>
                  <span className="font-semibold">Select items</span>
                </button>

                {/* Download */}
                <button
                  onClick={() => { handleDownloadAlbum(); setIsMobileMenuOpen(false); }}
                  disabled={isDownloading}
                  className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left text-white/90 hover:bg-white/8 transition-colors duration-200 disabled:opacity-50"
                >
                  <svg className="w-5 h-5 text-white/60 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  <span className="font-semibold">{isDownloading ? "Downloading…" : "Download album ZIP"}</span>
                </button>

                {/* Refresh */}
                <button
                  onClick={() => { handleRefresh(); setIsMobileMenuOpen(false); }}
                  disabled={mediaLoading}
                  className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left text-white/90 hover:bg-white/8 transition-colors duration-200 disabled:opacity-50"
                >
                  <svg className="w-5 h-5 text-white/60 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span className="font-semibold">Refresh</span>
                </button>

                {/* Memory Video */}
                {isMemoriesVideoEnabled && canRenameAlbum && (
                  <button
                    onClick={() => { handleMemoryVideoClick(); setIsMobileMenuOpen(false); }}
                    disabled={isMemoryVideoLoading || isCreatingMemoryVideo || isMemoryVideoProcessing}
                    className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left text-white/90 hover:bg-white/8 transition-colors duration-200 disabled:opacity-50"
                  >
                    <svg className="w-5 h-5 text-white/60 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14m-6 4h2a2 2 0 002-2V8a2 2 0 00-2-2H9a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span className="font-semibold">{memoryVideoButtonLabel}</span>
                  </button>
                )}
              </div>
            </div>
          </OverlayRoot>
        )}

        {/* Mobile upload FAB (shown only on mobile, not during upload/selection/viewer) */}
        {canUpload && !isPreparingUpload && !isSelectionMode && !isImageViewerOpen && (
          <button
            onClick={handleUploadClick}
            className="fixed bottom-6 right-5 z-30 sm:hidden flex items-center gap-2 pl-4 pr-5 py-3.5 rounded-2xl text-white font-semibold text-sm shadow-2xl shadow-black/40 bg-[linear-gradient(30deg,var(--primary-green-dark),var(--primary-green))] focus:outline-none focus:ring-2 focus:ring-[rgba(var(--primary-green-rgb),0.5)]"
            style={{ paddingBottom: "calc(0.875rem + env(safe-area-inset-bottom, 0px))" }}
            aria-label="Add photos or videos"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Photos
          </button>
        )}

        {/* Main Content */}
        <main
          className={[
            "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8",
            // The selection action bar is fixed at the bottom. When the actions menu is open, it occupies even more vertical
            // space (as an overlay), so we need extra bottom padding to ensure the last thumbnail rows can scroll fully into view.
            isSelectionMode && !isImageViewerOpen
              ? isActionsMenuOpen
                ? "pb-[calc(16rem+env(safe-area-inset-bottom))]"
                : "pb-[calc(8rem+env(safe-area-inset-bottom))]"
              : "pb-8",
          ].join(" ")}
          style={{ paddingTop: headerHeight + 24 }}
        >
          {/* Error Display */}
          {(mediaError || likesError || downloadError || downloadSelectionError || deleteSelectionError) && (
            <div data-section="album-detail-errors" className="mb-8">
              {deleteSelectionError && (
                <div className="bg-red-500/20 backdrop-blur-xl border border-red-400/30 text-red-100 px-6 py-4 rounded-2xl shadow-xl flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                    <span className="font-semibold">{deleteSelectionError}</span>
                  </div>
                  <button
                    onClick={clearDeleteSelectionError}
                    className="text-red-300 hover:text-red-100 hover:bg-red-500/20 p-2 rounded-xl transition-all duration-300"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
              {downloadSelectionError && (
                <div className="bg-red-500/20 backdrop-blur-xl border border-red-400/30 text-red-100 px-6 py-4 rounded-2xl shadow-xl flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                    <span className="font-semibold">{downloadSelectionError}</span>
                  </div>
                  <button
                    onClick={clearDownloadSelectionError}
                    className="text-red-300 hover:text-red-100 hover:bg-red-500/20 p-2 rounded-xl transition-all duration-300"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
              {downloadError && (
                <div className="bg-red-500/20 backdrop-blur-xl border border-red-400/30 text-red-100 px-6 py-4 rounded-2xl shadow-xl flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                    <span className="font-semibold">{downloadError}</span>
                  </div>
                  <button
                    onClick={clearDownloadError}
                    className="text-red-300 hover:text-red-100 hover:bg-red-500/20 p-2 rounded-xl transition-all duration-300"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
              {mediaError && (
                <div className="bg-red-500/20 backdrop-blur-xl border border-red-400/30 text-red-100 px-6 py-4 rounded-2xl shadow-xl flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                    <span className="font-semibold">{mediaError}</span>
                  </div>
                  <button
                    onClick={clearError}
                    className="text-red-300 hover:text-red-100 hover:bg-red-500/20 p-2 rounded-xl transition-all duration-300"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
              {likesError && (
                <div className="bg-red-500/20 backdrop-blur-xl border border-red-400/30 text-red-100 px-6 py-4 rounded-2xl shadow-xl flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                    <span className="font-semibold">{likesError}</span>
                  </div>
                  <button
                    onClick={clearLikesError}
                    className="text-red-300 hover:text-red-100 hover:bg-red-500/20 p-2 rounded-xl transition-all duration-300"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Media Gallery */}
          <ErrorBoundary onError={logError}>
            <MediaGallery
              media={media}
              totalMediaCount={visibleMediaCount}
              isFiltered={isUploaderFilterActive}
              onFilteredClick={openFilterMenu}
              onDeleteMedia={canDelete ? handleMediaDelete : undefined}
              isLoading={mediaLoading}
              isDeleting={isDeleting}
              isFetchingNextPage={isFetchingNextPage}
              hasNextPage={hasNextPage}
              fetchNextPage={fetchNextPage}
              loadedMediaCount={loadedMediaCount}
              showUploadButton={!isPreparingUpload && !hasMedia && canUpload && !isUploaderFilterActive}
              onUploadClick={canUpload ? handleUploadClick : undefined}
              onImageViewerStateChange={handleImageViewerStateChange}
              ownerId={album.ownerId}
              albumId={album.id}
              selectionMode={isSelectionMode}
              selectedMediaIds={selectedMediaIds}
              onSelectionModeChange={setSelectionModeEnabled}
              onToggleSelection={toggleMediaSelection}
              onApplySelectionRange={applyMediaSelectionRange}
              sortBy={sort.sortBy}
            />
          </ErrorBoundary>

        </main>
      </div>

      {/* Selection Action Bar */}
      {isSelectionMode && !isImageViewerOpen && (
        <div
          data-section="album-detail-selection-action-bar"
          className="fixed bottom-0 left-0 right-0 z-30 bg-black/60 backdrop-blur-xl border-t border-white/10"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            {/* Count + Done row */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-white font-semibold">
                {selectedMediaIds.size} selected
              </span>
              <button
                onClick={toggleSelectionMode}
                className="px-3.5 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white/80 hover:text-white text-sm font-semibold transition-all duration-200"
              >
                Done
              </button>
            </div>

            {/* Action buttons row */}
            <div className="flex items-stretch gap-2.5">
              <button
                onClick={selectAllLoaded}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-white/10 hover:bg-white/18 border border-white/15 text-white/80 hover:text-white text-sm font-medium transition-all duration-200"
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="2 -2 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 11l3 3L22 4" />
                </svg>
                <span>All</span>
              </button>

              <button
                onClick={clearSelection}
                disabled={selectedMediaIds.size === 0}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-white/10 hover:bg-white/18 border border-white/15 text-white/80 hover:text-white text-sm font-medium transition-all duration-200 disabled:opacity-40"
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>Clear</span>
              </button>

              <button
                onClick={handleDownloadSelection}
                disabled={selectedMediaIds.size === 0 || isDownloadingSelection}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-white/10 hover:bg-white/18 border border-white/15 text-white/80 hover:text-white text-sm font-medium transition-all duration-200 disabled:opacity-40"
              >
                {isDownloadingSelection ? (
                  <div className="w-4 h-4 border border-current border-t-transparent rounded-full animate-spin flex-shrink-0" />
                ) : (
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                )}
                <span>
                  {isDownloadingSelection
                    ? "Saving…"
                    : canNativeSaveSelection && selectedMediaIds.size <= MAX_NATIVE_SAVE_FILES
                      ? "Save"
                      : "Download"}
                </span>
              </button>

              {canDelete && (
                <button
                  onClick={openDeleteSelectionConfirm}
                  disabled={deletableSelectedIds.length === 0 || isDeletingSelection}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-red-500/15 hover:bg-red-500/25 border border-red-400/20 text-red-300 hover:text-red-200 text-sm font-medium transition-all duration-200 disabled:opacity-40"
                >
                  {isDeletingSelection ? (
                    <div className="w-4 h-4 border border-current border-t-transparent rounded-full animate-spin flex-shrink-0" />
                  ) : (
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  )}
                  <span>{isDeletingSelection ? "Deleting…" : "Delete"}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {isMemoriesVideoEnabled && isMemoryVideoModalOpen && (
        <OverlayRoot
          data-section="album-detail-memory-video-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overscroll-none"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              closeMemoryVideoModal();
            }
          }}
        >
          <div className="relative w-full max-w-xl overflow-hidden rounded-3xl border border-white/20 jp-sheet p-6 shadow-2xl backdrop-blur-xl">
            <div className="pointer-events-none absolute inset-0 jp-gradient-brand-soft opacity-70" />
            <div className="relative z-10">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-2xl font-bold text-white">Memories Video Template</h3>
                <p className="mt-2 text-sm text-gray-300">
                  Choose the style for this album video. If the backend soundtrack directory is configured,
                  the matching template soundtrack is used automatically.
                </p>
              </div>
              <button
                type="button"
                onClick={closeMemoryVideoModal}
                disabled={isCreatingMemoryVideo}
                className="rounded-xl p-2 text-gray-400 transition-colors duration-200 hover:bg-white/10 hover:text-white"
                aria-label="Close memories video modal"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {[
                {
                  id: "calm" as const,
                  title: "Calm",
                  description: "Longer clips, gentler pacing, softer transitions, ideal for family albums and travel timelines.",
                },
                {
                  id: "energetic" as const,
                  title: "Energetic",
                  description: "Quicker cuts, punchier pacing, stronger motion, ideal for party, sports, and highlight albums.",
                },
              ].map((option) => {
                const isSelected = selectedMemoryVideoTemplate === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setSelectedMemoryVideoTemplate(option.id)}
                    className={[
                      "rounded-2xl border p-5 text-left transition-all duration-200",
                      isSelected
                        ? "border-[rgba(var(--primary-green-rgb),0.6)] bg-[rgba(var(--primary-green-rgb),0.15)] text-white shadow-lg shadow-[rgba(var(--primary-blue-rgb),0.2)]"
                        : "border-white/15 bg-white/5 text-gray-200 hover:border-white/30 hover:bg-white/10",
                    ].join(" ")}
                  >
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <span className="text-lg font-semibold">{option.title}</span>
                      {isSelected && (
                        <svg className="h-5 w-5 text-[rgba(var(--primary-green-rgb),0.85)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <p className="text-sm leading-relaxed text-inherit/80">{option.description}</p>
                  </button>
                );
              })}
            </div>

            <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-gray-300">
              Regenerating replaces the latest job for this album with a new render using the selected template.
            </div>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={closeMemoryVideoModal}
                disabled={isCreatingMemoryVideo}
                className="jp-btn jp-btn--secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateMemoryVideo}
                disabled={isCreatingMemoryVideo}
                className="jp-btn jp-btn--primary flex-1"
              >
                {isCreatingMemoryVideo ? "Starting..." : "Generate Video"}
              </button>
            </div>
            </div>
          </div>
        </OverlayRoot>
      )}

      {/* Comments Bottom Sheet */}
      <CommentsBottomSheet
        ownerId={album.ownerId}
        albumId={album.id}
        albumName={album.name}
        isOpen={showComments}
        onClose={handleCommentsClose}
        canComment={canComment}
      />

      {/* Album Likes Modal */}
      <AlbumLikesModal
        isOpen={showLikes}
        onClose={handleLikesClose}
        likes={likes}
        albumName={album.name}
        isLoading={likesLoading}
        isLikedByCurrentUser={isLikedByCurrentUser}
        onLikeToggle={toggleLike}
        isLikeToggling={isLikeToggling}
        canLike={canLike}
      />

      {/* Share Links Modal */}
      <ShareLinksModal
        isOpen={showShareLinks}
        onClose={handleShareClose}
        albumName={album.name}
        albumId={album.id}
        ownerId={album.ownerId}
        links={links}
        isLoading={linksLoading}
        isCreating={isCreatingLink}
        isDeleting={isDeletingLink}
        error={linksError}
        onCreateLink={handleCreateShareLink}
        onDeleteLink={handleDeleteShareLink}
        onClearError={clearLinksError}
      />

      <DeleteMediaSelectionModal
        isOpen={isDeleteSelectionModalOpen}
        selectedCount={deletableSelectedIds.length}
        onClose={closeDeleteSelectionConfirm}
        onConfirm={handleConfirmDeleteSelection}
        isDeleting={isDeletingSelection}
      />

      {isRenameModalOpen && (
        <OverlayRoot data-section="album-rename-modal-root" className="fixed inset-0 z-[70] flex items-center justify-center px-4 overscroll-none">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeRenameModal} />
          <div className="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/30 bg-white/10 shadow-2xl backdrop-blur-xl">
            <div className="absolute inset-0 jp-gradient-brand-soft rounded-3xl opacity-80" />
            <div className="relative z-10 p-6">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl jp-icon-blue shadow-2xl">
                  <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Rename Album</h2>
                  <p className="mt-1 text-sm text-white/70">Choose a new name for this album.</p>
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="rename-album-name" className="mb-2 block text-sm font-semibold text-white">
                  Album Name
                </label>
                <input
                  id="rename-album-name"
                  type="text"
                  value={renameValue}
                  onChange={(event) => {
                    setRenameValue(event.target.value);
                    if (renameError) setRenameError(null);
                  }}
                  maxLength={100}
                  autoFocus
                  disabled={isRenamingAlbum}
                  className="w-full rounded-xl border border-white/25 bg-white/10 px-4 py-3 text-white focus:border-[rgba(var(--primary-blue-rgb),0.55)] focus:outline-none focus:ring-2 focus:ring-[rgba(var(--primary-blue-rgb),0.18)]"
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleRenameSubmit();
                    }
                    if (event.key === "Escape") {
                      event.preventDefault();
                      closeRenameModal();
                    }
                  }}
                />
                <div className="mt-2 text-right text-xs text-gray-300">{renameValue.length}/100</div>
                {renameError && <p className="mt-2 text-sm text-red-300">{renameError}</p>}
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={closeRenameModal}
                  disabled={isRenamingAlbum}
                  className="jp-btn jp-btn--secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleRenameSubmit}
                  disabled={isRenamingAlbum || !renameValue.trim()}
                  className="jp-btn jp-btn--primary flex-1"
                >
                  {isRenamingAlbum ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </OverlayRoot>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={uploadAcceptedTypes.join(",")}
        onChange={handleFileInputChange}
        className="hidden"
      />
      {showPrepareUi && (
        <UploadPrepareOverlay status={uploadPrepareStatus} onDismiss={cancelPicker} />
      )}
    </div>
  );
};

export default AlbumDetail;